/**
 * Compact Zone geometry preview for machine placement.
 * The saved coordinate determines membership; the selected Zone is context only.
 */
import { useEffect, useRef } from "react";
import maplibregl from "./vendor-maplibre.js";
import { html } from "./ui.js";
import * as D from "./data.js";

export function pointInZone(point, ring) {
  if (!Array.isArray(point) || !Array.isArray(ring) || ring.length < 3) return false;
  const x = Number(point[0]);
  const y = Number(point[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  let inside = false;
  const pts = ring[0] === ring[ring.length - 1] ? ring.slice(0, -1) : ring;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = Number(pts[i][0]);
    const yi = Number(pts[i][1]);
    const xj = Number(pts[j][0]);
    const yj = Number(pts[j][1]);
    const crosses = (yi > y) !== (yj > y)
      && x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

function boundsOf(ring) {
  if (!Array.isArray(ring) || !ring.length) return null;
  return ring.reduce((b, p) => {
    const x = Number(p && p[0]);
    const y = Number(p && p[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return b;
    if (!b) return [x, y, x, y];
    return [Math.min(b[0], x), Math.min(b[1], y), Math.max(b[2], x), Math.max(b[3], y)];
  }, null);
}

export default function ZoneMiniMap({
  zones = [],
  selectedId = "",
  point = null,
  onPoint,
  interactive = false,
}) {
  const host = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const ready = useRef(false);

  useEffect(() => {
    if (!host.current) return undefined;
    const m = new maplibregl.Map({
      container: host.current,
      style: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
      center: D.CENTRE,
      zoom: 14,
      attributionControl: { compact: true },
      interactive: true,
    });
    map.current = m;
    m.on("load", () => {
      m.addSource("zone-mini", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      m.addLayer({
        id: "zone-mini-fill", type: "fill", source: "zone-mini",
        paint: {
          "fill-color": ["case", ["get", "selected"], "#3DE1C8", "#6E8280"],
          "fill-opacity": ["case", ["get", "selected"], 0.22, 0.045],
        },
      });
      m.addLayer({
        id: "zone-mini-line", type: "line", source: "zone-mini",
        paint: {
          "line-color": ["case", ["get", "selected"], "#62E8CF", "#82908E"],
          "line-width": ["case", ["get", "selected"], 2.2, 1],
        },
      });
      ready.current = true;
    });
    return () => {
      marker.current && marker.current.remove();
      marker.current = null;
      ready.current = false;
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return undefined;
    const update = () => {
      const source = m.getSource("zone-mini");
      if (!source) return;
      const features = zones
        .filter((z) => Array.isArray(z && z.ring) && z.ring.length >= 3)
        .map((z) => ({
          type: "Feature",
          properties: {
            id: String(z.zone_id || z.id || ""),
            selected: String(z.zone_id || z.id || "") === String(selectedId || ""),
          },
          geometry: { type: "Polygon", coordinates: [z.ring] },
        }));
      source.setData({ type: "FeatureCollection", features });
      const selected = zones.find((z) => String(z.zone_id || z.id || "") === String(selectedId || ""));
      const bounds = boundsOf(selected && selected.ring);
      if (bounds) {
        m.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], {
          padding: 28, duration: 280, maxZoom: 17,
        });
      }
    };
    if (ready.current) update();
    else m.once("load", update);
    return () => m.off("load", update);
  }, [zones, selectedId]);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    marker.current && marker.current.remove();
    marker.current = null;
    if (!Array.isArray(point) || point.length < 2) return;
    const lng = Number(point[0]);
    const lat = Number(point[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const el = document.createElement("span");
    el.className = "zone-mini-map__pin";
    marker.current = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(m);
  }, [point]);

  useEffect(() => {
    const m = map.current;
    if (!m || !interactive || !onPoint) return undefined;
    const click = (event) => onPoint([Number(event.lngLat.lng), Number(event.lngLat.lat)]);
    m.on("click", click);
    m.getCanvas().style.cursor = "crosshair";
    return () => {
      m.off("click", click);
      m.getCanvas().style.cursor = "";
    };
  }, [interactive, onPoint]);

  return html`<div className="zone-mini-map" ref=${host}
    data-interactive=${interactive ? "1" : "0"} aria-label="Selected Zone map preview"/>`;
}
